import { EventType, SignalPanel, SleepStage, type SafeAny } from '@/common/types';
import { BaseD3, type D3Client, type GraphPoint, type Selection } from '@/common/ui';
import {
  BadDataSignal,
  applyArtifactBadData,
  formatTimeValue,
  i18n,
  isBadDataValueBySignal,
  formatFixed,
  MIN_SIGNAL_LANE_HEIGHT,
  MAX_SIGNAL_LANE_HEIGHT,
  type EventCallBackFn
} from '@/common/utils';
import type { SignalEvent } from '@/modules/epoch/data-access';
import { EpochGraphTypes, ScoreSetEvent, type SignalConfig } from '@/modules/epoch/types';
import { v4 as uuidv4 } from 'uuid';
import {
  axisBottom,
  axisLeft,
  BrushBehavior,
  BrushSelection,
  brushX,
  color,
  D3BrushEvent,
  D3DragEvent,
  drag,
  line,
  NumberValue,
  pointer,
  ScaleLinear,
  scaleLinear,
  schemeAccent,
  select,
  utcFormat,
  easeCubicOut
} from 'd3';
import { throttle } from 'lodash';
import {
  buildEventTooltipContent,
  buildLiveEventTooltipContent,
  getTranslatedValueWithArtifact
} from '@/core/helpers';
import { EVENT_TYPE_TO_SHORTCUT_ID } from './keyboard-shortcut-mapping';
import { setupYAxisDrag, type YAxisPanContext } from './helpers/signalYAxisPan';
import { signalAmplitudeRules } from './signalAmplitudeRules';

export enum Events {
  insertEvents = 'insert-events',
  updateEvents = 'update-events',
  deleteEvents = 'delete-events',
  deleteAllEvents = 'delete-all-events'
}

const EVENT_DETAIL_STATUS = 'setEventDetailStatus';

export const INVERTIBLE_SIGNAL_TITLES = new Set(['ecg', 'nasal_pressure']);

export const SIGNAL_GRAPH_CHART_LAYOUT = {
  fonts: { title: { fontSize: '11px', x: 80, y: 25 } },
  chartLayout: {
    margins: {
      top: 5,
      left: 68,
      right: 48,
      title: 8,
      yAxis: 30,
      zoomComponent: 38
    }
  }
} as const;

export const BAD_DATA_COLOR = '#FF9999';
export const ARTIFACT_COLOR_KEY = 'artifact';

export const baseEventColor: Record<string, string> = {
  [SleepStage.N1]: '#B0E0E6',
  [SleepStage.N2]: '#4682B4',
  [SleepStage.N3]: '#0047AB',
  [SleepStage.REM]: '#FF0000',
  [SleepStage.Wake]: '#FFC107',
  [SleepStage.Unstaged]: '#808080',
  [EventType.APNEA_CENTRAL]: '#FF0000',
  [EventType.APNEA_CENTRAL_RESP_FLOW]: '#FF0000',
  [EventType.APNEA_OBSTRUCTIVE]: '#FF4500',
  [EventType.APNEA_OBSTRUCTIVE_RESP_FLOW]: '#FF4500',
  [EventType.APNEA_MIXED]: '#9400FF',
  [EventType.APNEA_MIXED_RESP_FLOW]: '#9400FF',
  [EventType.HYPOPNEA_OBSTRUCTIVE]: '#1E90FF',
  [EventType.HYPOPNEA_OBSTRUCTIVE_RESP_FLOW]: '#1E90FF',
  [EventType.HYPOPNEA_CENTRAL]: '#FF1493',
  [EventType.HYPOPNEA_CENTRAL_RESP_FLOW]: '#FF1493',
  [EventType.RERA]: '#4B0082',
  [EventType.RERA_RESP_FLOW]: '#4B0082',
  [EventType.TACHYCARDIA]: '#90EE90',
  [EventType.BRADYCARDIA]: '#006400',
  [EventType.OTHER_ARRHYTHMIA]: '#FF0000',
  [EventType.ATRIAL_FIBRILLATION]: '#FF0000',
  [EventType.PAC]: '#FF0000',
  [EventType.PVC]: '#FF0000',
  [EventType.AV_BLOCK]: '#FF0000',
  [EventType.PACEMAKER_RHYTHM]: '#FF0000',
  [EventType.ASYSTOLE]: '#FF0000',
  [EventType.NARROW_COMPLEX_TACHYCARDIA]: '#FF0000',
  [EventType.WIDE_COMPLEX_TACHYCARDIA]: '#FF0000',
  [EventType.BRUXISM_MIX_L]: '#006400',
  [EventType.BRUXISM_MIX_R]: '#006400',
  [EventType.BRUXISM_PHASIC_L]: '#00008B',
  [EventType.BRUXISM_PHASIC_R]: '#00008B',
  [EventType.BRUXISM_TONIC_L]: '#300066',
  [EventType.BRUXISM_TONIC_R]: '#300066'
};

export class SignalGraphD3 extends BaseD3 implements D3Client {
  private studyStartSec = 0;
  private brushInitialized = false;
  private lastPlotW = -1;
  private lastPlotH = -1;
  private layoutRafPending = false;
  private renderRafPending = false;
  private lastXAxisKey = '';
  private lastTitleRendered = '';
  private singleLineGenerator = line<GraphPoint<SafeAny>>()
    .x((d) => Math.round(this.xScale(d.x) * 10) / 10)
    .defined((d) => d.y !== null && !isNaN(d.x) && d.y != null && !isNaN(d.y as number))
    .y((d) => Math.round(this.yScale(this.getInvertedY(d.y as number)) * 10) / 10);

  // Raw signal data for fast path rendering — avoids D3 line() accessor overhead
  private rawYData: SafeAny[] | null = null;
  private rawXBase = 0;
  private rawXTick = 0;

  declare protected data: GraphPoint<SafeAny>[];
  declare protected mappedData: GraphPoint<SafeAny>[];
  protected titleGroup?: Selection;
  protected laneResizeGuides: HTMLDivElement[] = [];
  protected laneResizeDrag?: {
    startY: number;
    startHeight: number;
    direction: 1 | -1;
    lastClientY: number;
    rafId: number | null;
    oppositeStartY: number;
    movingGuide: HTMLDivElement;
    oppositeGuide: HTMLDivElement;
    onMove: (event: MouseEvent) => void;
    onUp: (event: MouseEvent) => void;
  };
  protected events!: SignalEvent[];
  protected artifactEvents: Array<{ start: string; duration: number }> = [];
  protected acceptedEventTypes: string[] = [];
  protected startDatetime!: string;
  protected duration!: number;
  protected epoch!: number;
  protected selectedBrush: BrushSelection | null = null;
  protected mapping: SignalConfig = {
    min: 0,
    max: 0,
    defaultOffset: 0,
    defaultScale: 0,
    unit: '',
    vizmode: EpochGraphTypes.default
  };
  protected yScale!: ScaleLinear<SafeAny, SafeAny, SafeAny>;
  protected isEditable = true;
  protected isReadOnly = false;
  protected polarityInversion = false;

  protected gridConfig = {
    y: 6,
    x: 10
  };
  private readonly EPOCH_BOUNDARY_SEC = 30;
  private readonly EPOCH_ALIGNED_GRID_WINDOW_SIZES = new Set([60, 120, 300]);
  protected title!: string;
  protected readonly throttledUpdateSvgSizes;
  protected graphType!: EpochGraphTypes;
  protected color!: string;
  protected lineColor!: string;
  protected eventColors: Record<string, string> = {};
  protected shortcutKeys: Record<string, string> = {};
  protected artifactColor: string = BAD_DATA_COLOR;
  protected activeEvent: any;
  protected lastActiveEvent: any;
  protected BAD_DATA_COLOR = BAD_DATA_COLOR;
  protected isEventDragActive = false;
  protected onlyXAxis = false;
  private hasSubWindowIndicator = false;
  private isDualPanelAxis = false;
  private pane: SignalPanel | null = null;
  private subWindowIndicatorGroup?: Selection;
  private subWindowIndicatorRect?: Selection;
  private lastSubWindowEpoch = 0;
  private lastSubWindowSize = 0;
  protected areEventsEnabled = false;
  protected brush?: BrushBehavior<SafeAny>;
  protected brushGroup?: Selection;
  protected eventsGroup!: Selection;
  protected eventDetails?: Selection;
  protected respiratoryCollisionEvents?: ScoreSetEvent[];
  protected eventMove = drag();
  protected eventResize = drag();
  protected protectedEventTypes = ['N1', 'N2', 'N3', 'REM', 'Wake', 'Unstaged'];
  protected graphSettings = SIGNAL_GRAPH_CHART_LAYOUT;
  protected _dragStartY: number | null = null;
  protected _initialMapping: { min: number; max: number } | null = null;
  protected yAxisDragZone?: Selection;
  protected _onYAxisPanEnd?: (range: { min: number; max: number }) => void;
  public handleRestrictedEventUpdate?: () => void;

  constructor(svgRef: SVGElement) {
    super(svgRef);
    this.endBrushingHandler = this.endBrushingHandler.bind(this);
    this.duringBrushingHandler = this.duringBrushingHandler.bind(this);
    this.throttledUpdateSvgSizes = throttle(() => {
      this.scheduleLayoutUpdate();
    }, 100);
    this.eventDragSubjectHandler = this.eventDragSubjectHandler.bind(this);
    this.eventMovingHandler = this.eventMovingHandler.bind(this);
    this.eventStartMovingHandler = this.eventStartMovingHandler.bind(this);
    this.eventEndMovingHandler = this.eventEndMovingHandler.bind(this);
    this.showEventDetails = this.showEventDetails.bind(this);
    this.eventMouseEnterHandler = this.eventMouseEnterHandler.bind(this);
    this.clearEventDetails = this.clearEventDetails.bind(this);
    this.hideTooltip = this.hideTooltip.bind(this);
    this.handleGlobalMouseMove = this.handleGlobalMouseMove.bind(this);

    this.eventMove
      .subject(this.eventDragSubjectHandler as SafeAny)
      .on('start', this.eventStartMovingHandler as SafeAny)
      .on('drag', this.eventMovingHandler as SafeAny)
      .on('end', this.eventEndMovingHandler as SafeAny);

    this.eventStartResizingHandler = this.eventStartResizingHandler.bind(this);
    this.eventResizingHandler = this.eventResizingHandler.bind(this);
    this.eventEndResizingHandler = this.eventEndResizingHandler.bind(this);
    this.eventResize
      .subject(this.eventDragSubjectHandler as SafeAny)
      .on('start', this.eventStartResizingHandler as SafeAny)
      .on('drag', this.eventResizingHandler as SafeAny)
      .on('end', this.eventEndResizingHandler as SafeAny);
  }

  // Memoization cache for breakdownDataIntoCoords. Each call allocates one
  // {x,y} object per sample (~15K per signal × ~16 signals × 3 calls per paint
  // = ~720K short-lived objects per granular tick, which dominates the Major
  // GC pause observed during shift+arrow scrolling). The function is pure on
  // (yData, startDatetime, duration, epoch, isFullNight, measurementStart,
  // measurementEnd); during granular navigation within a single epoch all of
  // these inputs are stable, so caching the result by yData reference turns
  // every paint after the first into a zero-allocation lookup.
  //
  // WeakMap keyed on yData so cache entries are auto-evicted when the
  // underlying signal array drops out of the data-layer LRU. Consumers of the
  // returned array never mutate the {x,y} items (verified: applyArtifactBadData
  // returns a new array via .map() with spread; convertMultipleYValuesIntoLine
  // builds a fresh result; .filter() returns new arrays of refs), so it is
  // safe to hand back the cached reference directly without cloning.
  private static coordCache: WeakMap<
    object,
    {
      startDatetime: string;
      duration: number;
      epoch: any;
      isFullNight: boolean;
      measurementStart?: string;
      measurementEnd?: string;
      result: GraphPoint<any>[];
    }
  > = new WeakMap();

  static breakdownDataIntoCoords<T>(
    startDatetime: string | undefined,
    duration: number,
    yData: T[],
    epoch: any,
    isFullNight = false,
    measurementStart?: string,
    measurementEnd?: string
  ): GraphPoint<T>[] {
    if (!Array.isArray(yData) || yData.length === 0) return [];
    if (!startDatetime || Number.isNaN(new Date(startDatetime).getTime())) return [];

    const cacheKey = yData as unknown as object;
    const cached = SignalGraphD3.coordCache.get(cacheKey);
    if (
      cached &&
      cached.startDatetime === startDatetime &&
      cached.duration === duration &&
      cached.epoch === epoch &&
      cached.isFullNight === isFullNight &&
      cached.measurementStart === measurementStart &&
      cached.measurementEnd === measurementEnd
    ) {
      return cached.result as GraphPoint<T>[];
    }

    const startTimeSec = new Date(startDatetime).getTime() / 1000;
    let result: GraphPoint<T>[];

    // Sleep_Stages uses a length-1 dummy yData (`[[{0:{}}]]`); without the
    // `Math.max(..., 1)` floor below, `tick = total / 0 = Infinity` and then
    // `Infinity * 0 = NaN` makes every emitted x NaN. Most paths tolerate that
    // because Sleep_Stages renders via `updateEvents()` (driven by xScale, not
    // `this.data`), but during granular navigation paintChart filters the
    // merged adjacent+current coords through `p.x >= windowStart && p.x <=
    // windowEnd` — NaN comparisons are always false, so `data.length` becomes
    // 0 and `updateGraph` early-returns at line 744 without calling
    // `scheduleRender`. The result is a stuck Sleep_Stages channel during fast
    // wheel/granular scrolling.
    if (isFullNight && measurementStart && measurementEnd) {
      const measurementStartSec = new Date(measurementStart).getTime() / 1000;
      const measurementEndSec = new Date(measurementEnd).getTime() / 1000;
      const measurementDuration = measurementEndSec - measurementStartSec;

      const tick = measurementDuration / Math.max(yData.length - 1, 1);
      const fullPoints = yData.map((y, index) => ({
        x: measurementStartSec + tick * index,
        y
      }));

      const visibleStart = startTimeSec;
      const visibleEnd = visibleStart + duration;

      result = fullPoints.filter((p) => p.x >= visibleStart && p.x <= visibleEnd);
    } else {
      const tick = duration / Math.max(yData.length - 1, 1);
      const startDate = new Date(startDatetime);

      result = yData.map((item: T, index: number) => ({
        x: startDate.getTime() / 1000 + tick * index + epoch * duration,
        y: item
      }));
    }

    SignalGraphD3.coordCache.set(cacheKey, {
      startDatetime,
      duration,
      epoch,
      isFullNight,
      measurementStart,
      measurementEnd,
      result
    });

    return result;
  }

  public isWideWindow(): boolean {
    return this.duration === 600 || this.duration === 1800;
  }

  subscribeForInsertEvent(callback: EventCallBackFn) {
    this.subscriptions.add(this.eventBus.subscribe(Events.insertEvents, callback));
    return this;
  }

  subscribeForUpdateEvent(callback: EventCallBackFn) {
    this.subscriptions.add(this.eventBus.subscribe(Events.updateEvents, callback));
    return this;
  }

  subscribeForDeleteEvent(callback: EventCallBackFn) {
    this.subscriptions.add(this.eventBus.subscribe(Events.deleteEvents, callback));
    return this;
  }

  subscribeForEventDetailStatus(callback: EventCallBackFn) {
    this.subscriptions.add(this.eventBus.subscribe(EVENT_DETAIL_STATUS, callback));
    return this;
  }

  subscribeForDeleteAllEvents(callback: EventCallBackFn) {
    this.subscriptions.add(this.eventBus.subscribe(Events.deleteAllEvents, callback));
    return this;
  }

  setEditable(value: boolean): void {
    this.isEditable = value;
  }

  setReadOnly(value: boolean): void {
    this.isReadOnly = value;
  }

  setRespiratoryCollisionEvents(events: ScoreSetEvent[]): void {
    this.respiratoryCollisionEvents = events;
  }

  clearBrush() {
    if (this.brushGroup && this.brush) {
      this.brushGroup.call(this.brush.move, [0, 0]);
      this.selectedBrush = null;
    }
  }

  enableEvents() {
    this.areEventsEnabled = true;

    return this;
  }

  disableEvents() {
    this.areEventsEnabled = false;

    return this;
  }

  clearEvents(): void {
    if (!this.eventsGroup) return;

    if (this.activeEvent) {
      this.hideTooltip();
    }

    this.eventsGroup.selectAll('g.event').remove();
  }

  buildSvgElements() {
    if (this.onlyXAxis) {
      const existing = this.svg.select<SVGGElement>('g.x-axis');
      this.xAxisGroup = existing.empty() ? this.svg.append('g').classed('x-axis', true) : existing;

      if (this.hasSubWindowIndicator) {
        this.buildSubWindowIndicatorElements();
      }

      return this;
    }

    super.buildSvgElements();

    const existingXAxis = this.svg.select<SVGGElement>('g.x-axis');
    this.xAxisGroup = existingXAxis.empty()
      ? this.svg.append('g').classed('x-axis', true)
      : existingXAxis;

    this.yAxisGroup = this.svg.append('g').classed('y-axis', true);
    const existingTitleGroup = this.svg.select<SVGGElement>('g.title');
    this.titleGroup = existingTitleGroup.empty()
      ? this.svg.append('g').classed('title', true)
      : existingTitleGroup;
    this.graphGroup = this.svg.append('g').classed('chart', true);
    this.tooltipLayer = this.svg
      .append('rect')
      .classed('tooltip-layer', true)
      .attr(
        'x',
        this.graphSettings.chartLayout.margins.left +
          this.graphSettings.chartLayout.margins.yAxis +
          this.graphSettings.chartLayout.margins.title
      )
      .attr('y', this.graphSettings.chartLayout.margins.top);

    this.brushGroup = this.svg
      .append('g')
      .classed('brush', true)
      .attr(
        'transform',
        `translate(${
          this.graphSettings.chartLayout.margins.left +
          this.graphSettings.chartLayout.margins.yAxis +
          this.graphSettings.chartLayout.margins.title
        }, ${this.graphSettings.chartLayout.margins.top})`
      )
      .on('mouseenter', this.showTooltip)
      .on('mousemove', this.updateTooltip)
      .on('mouseleave', this.hideTooltip);

    this.eventsGroup = this.svg.append('g').classed('events', true);

    this.eventDetails = select('.event-details');
    if (!this.eventDetails.node()) {
      this.eventDetails = select('body').append('div').classed('event-details', true);
    }

    if (!this.areEventsEnabled) {
      this.tooltipLayer
        .on('mouseenter', this.showTooltip)
        .on('mousemove', this.updateTooltip)
        .on('mouseleave', this.hideTooltip);
    }
    return this;
  }

  enableOnlyXAxis() {
    this.onlyXAxis = true;
    return this;
  }

  enableSubWindowIndicator(): this {
    this.hasSubWindowIndicator = true;
    return this;
  }

  enableDualPanelAxis(): this {
    this.isDualPanelAxis = true;
    return this;
  }

  setPane(pane: SignalPanel | null): this {
    this.pane = pane;

    return this;
  }

  private get subWindowIndicatorColor(): string {
    return this.pane === SignalPanel.Bottom
      ? 'var(--color-top-accent)'
      : 'var(--color-success-accent)';
  }

  setSubWindowIndicatorVisibility(enabled: boolean): this {
    this.hasSubWindowIndicator = enabled;

    if (!enabled) {
      this.subWindowIndicatorGroup?.style('display', 'none');
      return this;
    }

    this.buildSubWindowIndicatorElements();
    this.subWindowIndicatorGroup?.style('display', null);
    this.updateSubWindowIndicator(this.lastSubWindowEpoch, this.lastSubWindowSize);

    return this;
  }

  private buildSubWindowIndicatorElements(): void {
    if (this.subWindowIndicatorGroup) return;

    const { left, yAxis, title, top } = this.graphSettings.chartLayout.margins;
    this.subWindowIndicatorGroup = this.svg
      .append('g')
      .classed('sub-window-indicator', true)
      .attr('transform', `translate(${left + yAxis + title - 2}, ${top})`);

    this.subWindowIndicatorRect = this.subWindowIndicatorGroup
      .append('rect')
      .attr('y', 0)
      .attr('height', Math.max(0, (this.svgSizes?.height ?? 0) - top))
      .attr('fill', this.subWindowIndicatorColor)
      .attr('fill-opacity', 0.3)
      .attr('stroke', this.subWindowIndicatorColor)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4 2')
      .attr('pointer-events', 'none');
  }

  updateSubWindowIndicator(subWindowEpoch: number, subWindowSize: number): void {
    this.lastSubWindowEpoch = subWindowEpoch;
    this.lastSubWindowSize = subWindowSize;

    if (!this.subWindowIndicatorRect || !this.xScale || !subWindowSize) return;

    const baseStartSec = Number.isFinite(this.studyStartSec)
      ? this.studyStartSec
      : this.startDatetime
        ? new Date(this.startDatetime).getTime() / 1000
        : NaN;
    if (!Number.isFinite(baseStartSec)) return;

    const start = baseStartSec + subWindowEpoch * subWindowSize;
    const end = start + subWindowSize;
    const x = this.xScale(start);
    this.subWindowIndicatorRect.attr('x', x).attr('width', Math.max(0, this.xScale(end) - x));
  }

  private renderEpochFrame() {
    if (this.onlyXAxis) {
      if (!this.xAxisGroup) return;

      if (this.svgSizes?.width < 1 || this.svgSizes?.height < 1) return;

      this.updateScales();

      const showXAxis = this.onlyXAxis;

      if (this.xAxisGroup) {
        this.xAxisGroup.style('display', (showXAxis ? null : 'none') as any);
      }

      if (showXAxis) {
        const xAxisKey = `${this.epoch}|${this.duration}|${this.studyStartSec}`;
        if (this.lastXAxisKey !== xAxisKey) {
          this.updateXAxis();
          this.lastXAxisKey = xAxisKey;
        }

        if (this.hasSubWindowIndicator) {
          this.updateSubWindowIndicator(this.lastSubWindowEpoch, this.lastSubWindowSize);
        }
      }
      return;
    }

    if (!this.graphGroup) return;

    if (this.svgSizes?.width < 1 || this.svgSizes?.height < 1) return;

    this.updateScales();
    this.updateYAxis();
    this.updateGrid();
    this.updateTitle();

    this.graphGroup.attr(
      'transform',
      `translate(${
        this.graphSettings.chartLayout.margins.left +
        this.graphSettings.chartLayout.margins.yAxis +
        this.graphSettings.chartLayout.margins.title
      }, ${this.graphSettings.chartLayout.margins.top})`
    );

    if (this.onlyXAxis) {
      this.updateXAxis();
      return;
    }

    // Render events BEFORE the empty-data early-return. Events live on their
    // own SVG group, are positioned via xScale.domain() (already up to date
    // from updateScales above), and don't read from `this.data`. More
    // importantly, `updateEvents` populates `event.startSec` (and
    // `_startCache`) on every event in `this.events` as a side effect of its
    // visibility filter — which downstream tooltip code (notably
    // Spo2SignalGraphD3.buildTooltipContent) reads via
    // `this.activeEvent.startSec`. If the empty-data guard short-circuits
    // before updateEvents runs, those side-effect fields stay undefined, and
    // hover tooltips on signals like SpO2 fail (the desaturation drop filter
    // becomes `p.x >= NaN`, returning nothing). Running events first
    // guarantees the side-effect properties are always available.
    if (this.areEventsEnabled) {
      this.updateEvents();
    }

    if (!this.data?.length) {
      return;
    }

    switch (this.graphType) {
      case EpochGraphTypes.bar:
        this.updateBars(this.lineColor);
        break;
      case EpochGraphTypes.oneLine:
        this.updateLine(this.lineColor);
        break;
      case EpochGraphTypes.multipleLines:
        this.updateMultipleLines();
        break;
      case EpochGraphTypes.scatter:
        this.updatePoint(this.lineColor);
        break;
    }
  }

  public insertEventByType(type: string) {
    const eventName = this.acceptedEventTypes.find((event) => event.includes(type));

    if (!type || !this.isEventDetailModalVisible() || !eventName) return;

    if (!this.selectedBrush) {
      this.updateEventByType(eventName);
      return;
    }

    // TODO: Refactor – centralize this logic with other duplicated operation
    const eventIndex = this.acceptedEventTypes.findIndex((item) => item.includes(type));
    const startDate = new Date(
      this.xScale.invert(this.selectedBrush?.[0] as number) * 1000
    ).toISOString();
    const duration =
      this.xScale.invert(this.selectedBrush?.[1] as number) -
      this.xScale.invert(this.selectedBrush?.[0] as number);
    const id = `temp_${uuidv4()}`;

    this.eventBus.publish(Events.insertEvents, {
      id,
      start: startDate,
      duration,
      eventIndex
    });
    this.clearEventSelection();
  }

  public scheduleLayoutUpdate() {
    if (this.layoutRafPending) return;
    this.layoutRafPending = true;
    requestAnimationFrame(() => {
      this.layoutRafPending = false;
      // Force-read layout when explicitly scheduled (title/graphType changed)
      this.updateSvgSizes(true);
    });
  }

  private scheduleRender() {
    if (this.renderRafPending) return;
    this.renderRafPending = true;
    requestAnimationFrame(() => {
      this.renderRafPending = false;
      this.renderEpochFrame();
    });
  }

  public updateEventByType(type: string) {
    if (!type || !this.isEventDetailModalVisible() || !this.lastActiveEvent) return;

    this.eventBus.publish(Events.updateEvents, {
      ...this.lastActiveEvent,
      eventType: type
    });

    this.clearEventSelection();
  }

  public deleteHoveredEvent() {
    const isDeletable =
      this.lastActiveEvent &&
      !this.protectedEventTypes.includes(this.lastActiveEvent.eventType) &&
      this.isEventDetailModalVisible();

    if (!isDeletable) return;

    this.eventBus.publish(Events.deleteEvents, { id: this.lastActiveEvent.id });
    this.clearEventSelection();
  }

  public setYAxisPanCallback(cb: (range: { min: number; max: number }) => void) {
    this._onYAxisPanEnd = cb;
  }

  private isEventDetailModalVisible(): boolean {
    const modalEl = document.querySelector<HTMLElement>('.event-details');

    if (!modalEl) return false;

    const style = window.getComputedStyle(modalEl);

    return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
  }

  /**
   * Build an SVG path string directly from the raw y-data array, computing x
   * inline from the linear timing parameters.  Avoids creating intermediate
   * GraphPoint objects and bypasses the D3 line() generator overhead.
   */
  private buildFastPath(): string {
    const yData = this.rawYData;
    if (!yData || yData.length === 0) return '';
    const xBase = this.rawXBase;
    const xTick = this.rawXTick;
    // Precompute linear scale slope/intercept once — avoids ~N D3 function calls per
    // render.  Both xScale and yScale are d3.scaleLinear so the mapping is affine:
    //   scale(v) = (v - d0) / (d1 - d0) * (r1 - r0) + r0
    const xDomain = this.xScale.domain();
    const xRange = this.xScale.range();
    const xSlope = (xRange[1] - xRange[0]) / (xDomain[1] - xDomain[0]);
    // px = xSlope * (xBase + xTick * i) + (xRange[0] - xDomain[0] * xSlope)
    //    = pxBase + pxStep * i
    const pxBase = xSlope * xBase + (xRange[0] - xDomain[0] * xSlope);
    const pxStep = xSlope * xTick;

    const yDomain = this.yScale.domain();
    const yRange = this.yScale.range();
    const ySlope = (yRange[1] - yRange[0]) / (yDomain[1] - yDomain[0]);
    const yIntercept = yRange[0] - yDomain[0] * ySlope;
    // Inline getInvertedY as a sign multiplier (no per-sample function call)
    const invSign =
      INVERTIBLE_SIGNAL_TITLES.has(this.title?.toLowerCase()) && this.polarityInversion ? -1 : 1;

    let path = '';
    let needMove = true;
    for (let i = 0; i < yData.length; i++) {
      const yRaw = yData[i];
      if (yRaw === null || yRaw === undefined || yRaw !== yRaw /* NaN */) {
        needMove = true;
        continue;
      }
      const px = Math.round((pxBase + pxStep * i) * 10) / 10;
      const py = Math.round((ySlope * (invSign * (yRaw as number)) + yIntercept) * 10) / 10;
      path += (needMove ? 'M' : 'L') + px + ',' + py;
      needMove = false;
    }
    return path;
  }

  /**
   * Fast path builder for multi-Y (min-max) downsampled data.
   * Builds a zigzag path (min→max→min→max…) directly from raw [min,max] pairs,
   * bypassing convertMultipleYValuesIntoLine (~15K object allocations) and D3 generators.
   */
  private buildFastMultiYPath(): string {
    const yData = this.rawYData;
    if (!yData || yData.length === 0) return '';
    const xBase = this.rawXBase;
    const xTick = this.rawXTick;
    const xScale = this.xScale;
    const yScale = this.yScale;
    let path = '';
    for (let i = 0; i < yData.length; i++) {
      const pair = yData[i];
      if (!Array.isArray(pair) || pair.length < 2) continue;
      const minVal = pair[0];
      const maxVal = pair[1];
      if (minVal !== minVal || maxVal !== maxVal) continue; // NaN check
      const px = Math.round(xScale(xBase + xTick * i) * 10) / 10;
      const pyMin = Math.round(yScale(this.getInvertedY(minVal)) * 10) / 10;
      const pyMax = Math.round(yScale(this.getInvertedY(maxVal)) * 10) / 10;
      // Draw vertical line from min to max at each x position
      path += (path ? 'L' : 'M') + px + ',' + pyMin + 'L' + px + ',' + pyMax;
    }
    return path;
  }

  updateGraph({
    color,
    data,
    duration,
    epoch,
    events,
    graphType,
    mapping,
    startDatetime,
    title,
    acceptedEventTypes,
    lineColor,
    eventColors,
    shortcutKeys,
    artifactColor,
    mappedData,
    polarityInversion,
    rawYData,
    rawXBase,
    rawXTick,
    artifactEvents
  }: {
    color?: string;
    data?: GraphPoint<SafeAny>[];
    duration: number;
    epoch: number;
    events?: SignalEvent[];
    graphType?: EpochGraphTypes;
    mapping?: SignalConfig;
    startDatetime: string;
    title?: string;
    acceptedEventTypes?: string[];
    lineColor?: string;
    eventColors?: Record<string, string>;
    shortcutKeys?: Record<string, string>;
    artifactColor?: string;
    mappedData?: GraphPoint<SafeAny>[];
    polarityInversion?: boolean;
    rawYData?: SafeAny[];
    rawXBase?: number;
    rawXTick?: number;
    artifactEvents?: Array<{ start: string; duration: number }>;
  }) {
    let requiresLayout = false;
    let requiresRender = false;

    if (this.onlyXAxis && this.xAxisGroup) {
      if (!this.lastXAxisKey) requiresRender = true;

      if (!this.svgSizes || this.svgSizes?.width < 1 || this.svgSizes?.height < 1)
        requiresLayout = true;
    }

    if (this.title !== title && title !== undefined) {
      this.title = title;
      requiresLayout = true;
    }
    if (this.graphType !== graphType) {
      this.graphType = graphType as EpochGraphTypes;
      requiresLayout = true;
    }

    if (this.data !== data) {
      this.data = data ?? [];
      requiresRender = true;
    }
    // Store raw signal data for fast path rendering (bypasses D3 line generator)
    this.rawYData = rawYData ?? null;
    this.rawXBase = rawXBase ?? 0;
    this.rawXTick = rawXTick ?? 0;

    if (this.mappedData !== mappedData) {
      this.mappedData = mappedData ?? [];
      requiresRender = true;
    }
    if (this.events !== events) {
      this.events = events ?? [];
      requiresRender = true;
    }
    this.artifactEvents = artifactEvents ?? [];
    if (this.duration !== duration) {
      this.duration = duration;
      requiresRender = true;
      requiresLayout = true;
    }
    if (this.epoch !== epoch) {
      this.epoch = epoch ?? 0;
      requiresRender = true;
    }
    if (this.mapping !== mapping) {
      this.mapping = (mapping as SignalConfig) || { min: 0, max: 0 };
      requiresLayout = true;
    }
    if (this.color !== color) {
      this.color = color ?? '';
      requiresRender = true;
    }
    if (this.lineColor !== lineColor) {
      this.lineColor = lineColor ?? '#000000';
      requiresRender = true;
    }
    if (this.eventColors !== eventColors) {
      this.eventColors = eventColors ?? {};
      requiresRender = true;
    }
    if (this.shortcutKeys !== shortcutKeys) {
      this.shortcutKeys = shortcutKeys ?? {};
    }
    if (this.artifactColor !== artifactColor) {
      this.artifactColor = artifactColor ?? BAD_DATA_COLOR;
      requiresRender = true;
    }
    if (this.acceptedEventTypes !== acceptedEventTypes) {
      this.acceptedEventTypes = acceptedEventTypes ?? [];
      requiresRender = true;
    }

    if (this.polarityInversion !== polarityInversion) {
      this.polarityInversion = Boolean(polarityInversion);
      requiresRender = true;
    }

    if (this.startDatetime !== startDatetime) {
      this.startDatetime = startDatetime;
      this.studyStartSec = new Date(startDatetime).getTime() / 1000;
      requiresRender = true;
    }

    if (!this.data?.length && !this.onlyXAxis) {
      if (this.titleGroup) {
        const needsTitle =
          this.lastTitleRendered !== (this.title ?? '') ||
          this.titleGroup.selectAll('text').empty();

        if (needsTitle) {
          this.updateTitle();
          this.lastTitleRendered = this.title ?? '';
        }
      }
      return this;
    }

    if (requiresLayout) {
      this.scheduleLayoutUpdate();
    }

    if (requiresRender || requiresLayout) {
      this.scheduleRender();
    }

    return this;
  }

  cleanUp(): void {
    this.cancelLaneResizePreview();
    super.cleanUp();
  }

  protected updateBars(color: string) {
    const averageY = (this.mapping.min + this.mapping.max) / 2;
    const bars = this.graphGroup.selectAll('line').data(this.data);
    bars
      .enter()
      .append('line')
      .attr('x1', (d) => this.xScale(d.x))
      .attr('x2', (d) => this.xScale(d.x))
      .attr('y1', this.yScale(averageY))
      .attr('y2', this.yScale(averageY))
      .merge(bars as SafeAny)
      .attr('stroke', color)
      .attr('stroke-width', 2)
      .attr('x1', (d) => this.xScale(d.x))
      .attr('x2', (d) => this.xScale(d.x))
      .attr('y1', (d) => this.yScale(d.y[0]))
      .attr('y2', (d) => this.yScale(d.y[1]));
    bars.exit().remove();
  }

  protected updateLine(color: string): void {
    if (!this.graphGroup || !this.xScale || !this.yScale) return;

    const isMultiY = Array.isArray((this.data as any)?.[0]?.y);
    // Use fast path builder when raw single-value data is available
    const useFastPath = !isMultiY && this.rawYData !== null && this.rawYData.length > 0;
    // Use fast multi-Y path when raw data contains [min,max] pairs (downsampled signals)
    const useMultiYFastPath =
      isMultiY &&
      this.rawYData !== null &&
      this.rawYData.length > 0 &&
      Array.isArray(this.rawYData[0]);

    // For fast path: use this.data directly (already a real coord array from breakdownDataIntoCoords).
    // For multi-Y slow path: expand [min,max] pairs into a flat point list for D3's line generator.
    let lineData: GraphPoint<SafeAny>[] = useFastPath
      ? (this.data ?? [])
      : useMultiYFastPath
        ? []
        : isMultiY
          ? this.convertMultipleYValuesIntoLine()
          : (this.data ?? []);

    if (this.artifactEvents?.length) {
      lineData = applyArtifactBadData(lineData, this.artifactEvents);
    }

    let pathString: string | null = null;
    if (useFastPath) {
      pathString = this.buildFastPath();
    } else if (useMultiYFastPath) {
      pathString = this.buildFastMultiYPath();
    }

    const path = this.graphGroup
      .selectAll<SVGPathElement, GraphPoint<SafeAny>[]>('path.single-line')
      .data([lineData]);

    path
      .join(
        (enter) => enter.append('path').attr('class', 'single-line').attr('fill', 'none'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('stroke', color)
      .attr('d', pathString !== null ? pathString : (this.singleLineGenerator as SafeAny));

    const badDataPoints = lineData.filter((d) => d.y === null && Number.isFinite(d.x));
    const badMarkers = this.graphGroup
      .selectAll<SVGCircleElement, GraphPoint<SafeAny>>('circle.bad-marker')
      .data(badDataPoints, (d, i) => `${d.x}-${i}`);

    badMarkers
      .join(
        (enter) =>
          enter.append('circle').classed('bad-marker', true).attr('r', 1).attr('fill', 'darkgray'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('cx', (d) => this.xScale(d.x))
      .attr('cy', () => this.yScale(this.yScale.domain()[0]));

    if (badDataPoints.length > 0) {
      this.drawBadDataLines(lineData);
    } else {
      this.graphGroup.selectAll('.bad-segment-line').remove();
    }
  }

  protected updateTooltip(event: MouseEvent): void {
    if (this.isEventDragActive) return;

    const svgRect = this.container.getBoundingClientRect();
    const relativeX =
      event.clientX -
      svgRect.left -
      (this.graphSettings.chartLayout.margins.left +
        this.graphSettings.chartLayout.margins.yAxis +
        this.graphSettings.chartLayout.margins.title);

    const hoveredTime = this.xScale.invert(relativeX);

    const data = this.findTheClosesPoint(hoveredTime);

    if (!data) return;

    this.tooltip
      .style('top', `${event.clientY + 5}px`)
      .style('left', `${event.clientX + 5}px`)
      .html(this.buildTooltipContent(data));

    requestAnimationFrame(() => {
      this.tooltip.style(
        'transform',
        event.clientX + (this.tooltip.node()?.getBoundingClientRect().width || 0) >
          window.innerWidth - 20
          ? `translateX(-100%)`
          : ''
      );
    });
  }

  protected drawBadDataLines(lineData: GraphPoint<SafeAny>[]): void {
    this.graphGroup.selectAll('.bad-segment-line').remove();

    const badSegments: { start: number; end: number }[] = [];
    let currentStart: number | null = null;

    lineData.forEach((d, i) => {
      if (d.y === null) {
        if (currentStart === null) currentStart = d.x;
      } else if (currentStart !== null) {
        badSegments.push({ start: currentStart, end: lineData[i - 1].x });
        currentStart = null;
      }
    });
    if (currentStart !== null) {
      badSegments.push({ start: currentStart, end: lineData[lineData.length - 1].x });
    }

    const yMin = this.yScale(this.yScale.domain()[0]);
    const yMax = this.yScale(this.yScale.domain()[1]);

    badSegments.forEach((seg) => {
      this.graphGroup
        .append('line')
        .attr('class', 'bad-segment-line')
        .attr('x1', this.xScale(seg.start))
        .attr('x2', this.xScale(seg.start))
        .attr('y1', yMin)
        .attr('y2', yMax)
        .attr('stroke', 'darkgray')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2');

      this.graphGroup
        .append('line')
        .attr('class', 'bad-segment-line')
        .attr('x1', this.xScale(seg.end))
        .attr('x2', this.xScale(seg.end))
        .attr('y1', yMin)
        .attr('y2', yMax)
        .attr('stroke', 'darkgray')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2,2');
    });
  }

  applyLeftBarGradient() {
    if (!this.eventsGroup) return;

    this.svg.selectAll('.gradient-overlay').remove();
    this.svg.selectAll('defs').remove();

    const gradient = this.svg
      .append('defs')
      .append('linearGradient')
      .attr('id', 'leftGradient')
      .attr('x1', '0%')
      .attr('y1', '0%')
      .attr('x2', '100%')
      .attr('y2', '0%');

    gradient
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#fff')
      .attr('stop-opacity', 0.8);

    gradient
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#fff')
      .attr('stop-opacity', 0.8);

    this.eventsGroup
      .append('rect')
      .attr('width', '120px')
      .attr('display', 'flex')
      .attr('class', 'gradient-overlay')
      .attr('height', '100%')
      .style('fill', 'url(#leftGradient)')
      .attr('transform', 'translate(-120, 0)');
  }

  protected updateMultipleLines() {
    const seriesCount = Array.isArray(this.data[0]?.y) ? this.data[0].y.length : 0;
    const seriesIdx = Array.from({ length: seriesCount }, (_, i) => i);

    const groups = this.graphGroup.selectAll('g.multiple-line').data(seriesIdx);

    const groupsJoin = groups.join(
      (enter) => enter.append('g').classed('multiple-line', true),
      (update) => update,
      (exit) => exit.remove()
    );

    const paths = groupsJoin.selectAll('path').data((d) => [d]);

    paths
      .join(
        (enter) => enter.append('path').attr('fill', 'none'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('stroke', (i) => schemeAccent[i])
      .attr('d', (i: number) => {
        let out = '';
        for (let j = 0; j < this.data.length; j++) {
          const p = this.data[j] as any;
          const xRaw = (p as any).x;
          if (!Number.isFinite(xRaw)) continue;
          const x = Math.round(this.xScale(xRaw) * 10) / 10;
          if (!Number.isFinite(x)) continue;
          const yv = p.y?.[i];
          if (yv == null || isNaN(yv)) continue;
          const y = Math.round(this.yScale(yv) * 10) / 10;
          if (!Number.isFinite(y)) continue;
          out += (out ? 'L' : 'M') + x + ',' + y;
        }
        return out;
      });
  }

  protected updatePoint(color: string, radius = 1.5) {
    if (!this.graphGroup || !this.xScale || !this.yScale) return;

    const { min, max } = this.getMinAndMax();
    const averageY = (min + max) / 2;

    const isMultiY = Array.isArray((this.data as any)?.[0]?.y);
    const pointData: GraphPoint<SafeAny>[] = isMultiY
      ? this.convertMultipleYValuesIntoLine()
      : (this.data ?? []);

    const points = this.graphGroup
      .selectAll<SVGCircleElement, GraphPoint<SafeAny>>('circle.single-line')
      .data(pointData);

    points
      .enter()
      .append('circle')
      .classed('single-line', true)
      .attr('cx', (d) => this.xScale(d.x))
      .attr('cy', this.yScale(averageY))
      .merge(points as SafeAny)
      .attr('fill', color)
      .attr('cx', (d) => this.xScale(d.x))
      .attr('cy', (d) =>
        d.y == null ? this.yScale(averageY) : this.yScale(this.getInvertedY(d.y))
      )
      .attr('r', radius);

    points.exit().remove();
  }

  protected updateXAxis(): void {
    if (!this.startDatetime) return;

    const tick = this.duration / this.gridConfig.x;
    const startDate = new Date(this.startDatetime);
    const xGridData = [...Array(this.gridConfig.x + 1).keys()].map(
      (index) => startDate.getTime() / 1000 + tick * index + this.duration * this.epoch
    );

    const {
      left: axLeft,
      yAxis: axYAxis,
      title: axTitle,
      top: axTop
    } = this.graphSettings.chartLayout.margins;
    this.xAxisGroup
      .attr('transform', `translate(${axLeft + axYAxis + axTitle - 2}, ${axTop})`)
      .call(
        axisBottom(this.xScale)
          .tickValues(xGridData)
          .tickFormat((d) => utcFormat('%H:%M:%S')(new Date(+d * 1000)))
      );

    this.xAxisGroup.selectAll('.tick text').style('font-size', '12px').style('color', 'grey');
  }

  protected updateAxis() {
    if (!this.data.length) return;

    this.updateScales();
    this.updateYAxis();
    this.updateGrid();
    this.updateTitle();
    this.graphGroup.attr(
      'transform',
      `translate(${
        this.graphSettings.chartLayout.margins.left +
        this.graphSettings.chartLayout.margins.yAxis +
        this.graphSettings.chartLayout.margins.title
      }, ${this.graphSettings.chartLayout.margins.top})`
    );

    switch (this.graphType) {
      case EpochGraphTypes.bar:
        this.updateBars(this.lineColor);
        break;
      case EpochGraphTypes.oneLine:
        this.updateLine(this.lineColor);
        break;
      case EpochGraphTypes.multipleLines:
        this.updateMultipleLines();
        break;
      case EpochGraphTypes.scatter:
        this.updatePoint(this.lineColor);
        break;
    }
  }

  protected convertMultipleYValuesIntoLine(mappedData?: any) {
    const data = this.data;
    const title = this.title as BadDataSignal;
    const hasMapped = !!mappedData;

    // Pre-allocate: most multi-Y entries have 2 values (min-max pairs)
    const stride = data.length > 0 && Array.isArray(data[0]?.y) ? data[0].y.length : 2;
    const result = new Array<GraphPoint<SafeAny>>(data.length * stride);
    let writeIdx = 0;
    const tolerance = data.length > 1 ? Math.abs(data[1].x - data[0].x) / 2 : Infinity;
    let mappedIndex = 0;

    for (let index = 0; index < data.length; index += 1) {
      const item = data[index];
      const rawY = item.y;

      let mappedSource: SafeAny = null;
      if (hasMapped) {
        while (
          mappedIndex < this.mappedData.length - 1 &&
          this.mappedData[mappedIndex + 1].x <= item.x
        ) {
          mappedIndex += 1;
        }
        const candidate = this.mappedData[mappedIndex];
        if (candidate && Math.abs(candidate.x - item.x) <= tolerance) {
          mappedSource = candidate.y;
        }
      }
      const mappedIsArray = Array.isArray(mappedSource);
      const mappedFallback = mappedIsArray ? mappedSource?.[0] : mappedSource;

      for (let i = 0; i < rawY.length; i += 1) {
        const value = rawY[i];
        result[writeIdx++] = {
          x: item.x,
          y: isBadDataValueBySignal(title, value) ? null : value,
          rawY,
          combinedY: hasMapped
            ? mappedIsArray
              ? (mappedSource?.[i] ?? mappedFallback)
              : mappedFallback
            : undefined
        };
      }
    }

    // Trim in case some entries had different stride
    if (writeIdx < result.length) result.length = writeIdx;

    return result;
  }
  private hasValidTimeWindow(): boolean {
    const epoch =
      typeof (this as any).epoch === 'string' ? Number((this as any).epoch) : (this as any).epoch;

    const duration =
      typeof (this as any).duration === 'string'
        ? Number((this as any).duration)
        : (this as any).duration;

    if (!this.startDatetime && !Number.isFinite(this.studyStartSec)) return false;
    if (!Number.isFinite(duration) || duration <= 0) return false;
    if (!Number.isFinite(epoch) || epoch < 0) return false;

    const baseStartSec = Number.isFinite(this.studyStartSec)
      ? this.studyStartSec
      : new Date(this.startDatetime).getTime() / 1000;

    return Number.isFinite(baseStartSec);
  }

  protected updateSvgSizes(forceRead = false) {
    super.updateSvgSizes(forceRead);

    if (this.onlyXAxis && this.xAxisGroup) {
      this.updateScales();
      this.updateXAxis();

      if (this.hasSubWindowIndicator) {
        this.subWindowIndicatorRect?.attr(
          'height',
          Math.max(0, this.svgSizes.height - this.graphSettings.chartLayout.margins.top)
        );
        this.updateSubWindowIndicator(this.lastSubWindowEpoch, this.lastSubWindowSize);
      }
      return;
    }

    if (!this.graphGroup || !this.tooltipLayer) return;

    if (!this.svgSizes || this.svgSizes.width < 1 || this.svgSizes.height < 1) return;

    const plotW =
      this.svgSizes.width -
      (this.graphSettings.chartLayout.margins.left +
        this.graphSettings.chartLayout.margins.yAxis +
        this.graphSettings.chartLayout.margins.title) -
      30;

    const plotH = this.svgSizes.height - this.graphSettings.chartLayout.margins.top * 2;

    // Tooltip layer size depends on pixels -> update on resize only.
    this.tooltipLayer.attr('width', plotW).attr('height', plotH);

    if (!this.onlyXAxis) {
      if (!this.brushInitialized || !this.brush) {
        this.brush = brushX()
          .on('brush', this.duringBrushingHandler)
          .on('end', this.endBrushingHandler);

        if (this.brushGroup && !this.brushGroup.empty()) {
          this.brushGroup.call(this.brush);
          this.brushInitialized = true;
        }
      }

      const sizeChanged = plotW !== this.lastPlotW || plotH !== this.lastPlotH;

      if (this.brush && sizeChanged) {
        this.brush.extent([
          [0, 0],
          [plotW, plotH]
        ]);

        if (this.brushGroup && !this.brushGroup.empty()) {
          this.brushGroup.call(this.brush);
        }

        this.lastPlotW = plotW;
        this.lastPlotH = plotH;
      }

      // Re-render the signal line/bars/etc. when the plot box changes shape.
      // updateScales below refreshes the xScale/yScale ranges, but the actual
      // path was emitted earlier as a static SVG `d` attribute computed from
      // the previous range — so without an explicit render, axes/grid land at
      // the new boundaries while the line ends at the old one (visible as
      // signals truncating horizontally after a viewport resize, DevTools
      // close, or a full-night signal being added below the hypnogram).
      // Per-epoch signals get a fresh paint on the next epoch navigation,
      // but full-night panels (epoch=0 constant) never repaint from
      // navigation — they would otherwise stay broken until the user
      // resizes the actual window again.
      if (sizeChanged) {
        this.scheduleRender();
      }
    }

    this.updateTitle();
    this.updateLaneResizeZones();

    if (this.onlyXAxis) {
      this.updateXAxis();
    } else {
      if (this.hasValidTimeWindow()) {
        this.updateScales();
      }
      this.updateYAxis();
      this.updateGrid();
    }
  }

  protected updateTitle(): void {
    if (!this.titleGroup) return;
    this.titleGroup
      .attr('transform', `translate(8, ${this.graphSettings.chartLayout.margins.top})`)
      .style('position', 'absolute')
      .style('z-index', '2')
      .raise();

    const title = this.titleGroup.selectAll<SVGTextElement, number>('text.chart-title').data([1]);

    const shouldApplySmallFonts = this.svgSizes?.height <= 45 || this.title === 'Sleep_Stages';

    let xPosition = shouldApplySmallFonts ? 70 : this.graphSettings.fonts.title.x;
    let yPosition = shouldApplySmallFonts ? 18 : this.graphSettings.fonts.title.y;

    if (this.title === 'Position') {
      xPosition -= 8;
    }

    if (this.svgSizes?.height < 25) {
      yPosition -= 4;
    }

    title
      .enter()
      .append('text')
      .classed('chart-title', true)
      .merge(title as SafeAny)
      .attr('x', xPosition)
      .attr('y', yPosition)
      .attr('text-anchor', 'end')
      .text(this.title)
      .style('font-size', shouldApplySmallFonts ? '7px' : this.graphSettings.fonts.title.fontSize)
      .style('text-transform', 'capitalize')
      .style('font-weight', 'bold')
      .style('pointer-events', 'none')
      .attr('fill', 'black');

    title.exit().remove();

    if (this.title !== 'Sleep_Stages' && this.title !== 'Position') {
      // Zoom controls
      const controls = this.titleGroup.selectAll<SVGGElement, number>('g.zoom-controls').data([1]);

      const controlsEnter = controls
        .enter()
        .append('g')
        .attr('class', 'zoom-controls')
        .style('cursor', 'pointer');

      const controlsMerge = controlsEnter.merge(controls as SafeAny);
      // Plus Zoom
      const plus = controlsMerge.selectAll<SVGGElement, number>('g.zoom-plus').data([1]).raise();

      const plusEnter = plus.enter().append('g').attr('class', 'zoom-plus');

      plusEnter
        .append('rect')
        .attr('width', 10)
        .attr('height', 10)
        .attr('rx', 0)
        .attr('ry', 4)
        .attr('fill', 'rgba(0,0,0,0.3)');

      plusEnter
        .append('text')
        .attr('x', 5)
        .attr('y', 8)
        .attr('text-anchor', 'middle')
        .text('+')
        .attr('fill', 'white')
        .style('font-size', '10px')
        .style('font-weight', 'bold');

      plusEnter
        .merge(plus as SafeAny)
        .attr('transform', `translate(0, 0)`)
        .on('click', () => {
          this.zoomIn();
        });

      plus.exit().remove();

      // Minus Zoom
      const minus = controlsMerge.selectAll<SVGGElement, number>('g.zoom-minus').data([1]).raise();

      const minusEnter = minus.enter().append('g').attr('class', 'zoom-minus');

      minusEnter
        .append('rect')
        .attr('width', 10)
        .attr('height', 10)
        .attr('rx', 0)
        .attr('ry', 4)
        .attr('fill', 'rgba(0,0,0,0.3)');

      minusEnter
        .append('text')
        .attr('x', 5)
        .attr('y', 8)
        .attr('text-anchor', 'middle')
        .text('-')
        .attr('fill', 'white')
        .style('font-size', '10px')
        .style('font-weight', 'bold');

      minusEnter
        .merge(minus as SafeAny)
        .attr('transform', `translate(15, 0)`)
        .on('click', () => {
          this.zoomOut();
        });

      minus.exit().remove();
      controls.exit().remove();
    }
  }

  protected updateLaneResizeZones(): void {
    if (this.title === 'Sleep_Stages' || !this.svgSizes) return;

    const { left, yAxis, title } = this.graphSettings.chartLayout.margins;
    const zoneWidth = left + yAxis + title - 2;
    const zoneHeight = 6;

    const zones = this.svg
      .selectAll<SVGRectElement, { direction: 1 | -1 }>('rect.lane-resize-zone')
      .data([{ direction: -1 }, { direction: 1 }] as { direction: 1 | -1 }[]);

    zones
      .enter()
      .append('rect')
      .attr('class', 'lane-resize-zone')
      .attr('x', 0)
      .attr('fill', 'transparent')
      .style('cursor', 'row-resize')
      .on('mousedown', (event: MouseEvent, d) => {
        event.preventDefault();
        event.stopPropagation();
        this.startLaneResizePreview(event, d.direction);
      })
      .merge(zones as SafeAny)
      .attr('width', zoneWidth)
      .attr('height', zoneHeight)
      .attr('y', (d) => (d.direction === -1 ? 0 : this.svgSizes.height - zoneHeight))
      .raise();

    zones.exit().remove();
  }

  private appendLaneResizeGuide(top: number): HTMLDivElement {
    const guide = document.createElement('div');
    guide.className = 'lane-resize-guide';
    guide.style.cssText = [
      'position: fixed',
      'left: 0',
      'right: 0',
      'height: 0',
      'border-top: 1px dashed #1e201e',
      'pointer-events: none',
      'z-index: 2000'
    ].join(';');
    guide.style.top = `${top}px`;
    document.body.appendChild(guide);
    this.laneResizeGuides.push(guide);

    return guide;
  }

  private laneResizeHeight(clientY: number): number {
    const drag = this.laneResizeDrag;
    if (!drag) return 0;

    const requested = drag.startHeight + 2 * (clientY - drag.startY) * drag.direction;

    return Math.min(MAX_SIGNAL_LANE_HEIGHT, Math.max(MIN_SIGNAL_LANE_HEIGHT, requested));
  }

  private laneResizeDelta(clientY: number): number {
    const drag = this.laneResizeDrag;
    if (!drag) return 0;

    return ((this.laneResizeHeight(clientY) - drag.startHeight) / 2) * drag.direction;
  }

  protected startLaneResizePreview(event: MouseEvent, direction: 1 | -1): void {
    if (this.laneResizeDrag) return;

    const startY = event.clientY;
    const startHeight = this.svgSizes.height;

    const laneRect = (this.svg.node() as SVGSVGElement | null)?.getBoundingClientRect();
    const oppositeStartY = (direction === 1 ? laneRect?.top : laneRect?.bottom) ?? startY;

    const movingGuide = this.appendLaneResizeGuide(startY);
    const oppositeGuide = this.appendLaneResizeGuide(oppositeStartY);

    const onMove = (moveEvent: MouseEvent) => {
      const drag = this.laneResizeDrag;
      if (!drag) return;

      drag.lastClientY = moveEvent.clientY;
      if (drag.rafId !== null) return;
      drag.rafId = requestAnimationFrame(() => {
        const current = this.laneResizeDrag;
        if (!current) return;

        current.rafId = null;

        const delta = this.laneResizeDelta(current.lastClientY);
        current.movingGuide.style.top = `${current.startY + delta}px`;
        current.oppositeGuide.style.top = `${current.oppositeStartY - delta}px`;
      });
    };

    const onUp = (upEvent: MouseEvent) => {
      this.endLaneResizePreview(upEvent.clientY);
    };

    this.laneResizeDrag = {
      startY,
      startHeight,
      direction,
      lastClientY: startY,
      rafId: null,
      oppositeStartY,
      movingGuide,
      oppositeGuide,
      onMove,
      onUp
    };

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  protected cancelLaneResizePreview(): void {
    const drag = this.laneResizeDrag;
    if (!drag) return;

    if (drag.rafId !== null) cancelAnimationFrame(drag.rafId);
    document.removeEventListener('mousemove', drag.onMove);
    document.removeEventListener('mouseup', drag.onUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    this.laneResizeGuides.forEach((guide) => guide.remove());
    this.laneResizeGuides = [];
    this.laneResizeDrag = undefined;
  }

  protected endLaneResizePreview(clientY: number): void {
    const drag = this.laneResizeDrag;
    if (!drag) return;

    const height = Math.round(this.laneResizeHeight(clientY));

    this.cancelLaneResizePreview();

    if (height === Math.round(drag.startHeight)) return;

    window.dispatchEvent(
      new CustomEvent('lane-resize-end', {
        detail: { signal: this.title, height }
      })
    );
  }

  public zoomIn(): void {
    window.dispatchEvent(new CustomEvent('signal-zoom-in', { detail: this.title }));
  }

  public zoomOut(): void {
    window.dispatchEvent(new CustomEvent('signal-zoom-out', { detail: this.title }));
  }

  protected updateScales(): void {
    const epoch =
      typeof (this as any).epoch === 'string' ? Number((this as any).epoch) : (this as any).epoch;

    const duration =
      typeof (this as any).duration === 'string'
        ? Number((this as any).duration)
        : (this as any).duration;

    const baseStartSec = Number.isFinite(this.studyStartSec)
      ? this.studyStartSec
      : this.startDatetime
        ? new Date(this.startDatetime).getTime() / 1000
        : NaN;

    if (
      !Number.isFinite(baseStartSec) ||
      !Number.isFinite(duration) ||
      duration <= 0 ||
      !Number.isFinite(epoch) ||
      epoch < 0
    ) {
      return;
    }

    const startSec = baseStartSec + duration * epoch;
    const endSec = startSec + duration;

    const widthForScale = this.isDualPanelAxis ? this.svgSizes.width + 20 : this.svgSizes.width;

    const plotW =
      widthForScale -
      30 -
      this.graphSettings.chartLayout.margins.left * 2 -
      this.graphSettings.chartLayout.margins.yAxis;

    this.xScale.domain([startSec, endSec]).range([0, plotW]);

    if (this.onlyXAxis) return;

    const { min, max } = this.getMinAndMax();
    const height = this.svgSizes?.height - this.graphSettings.chartLayout.margins.top * 2;

    if (!this.yScale) {
      this.yScale = scaleLinear().domain([min, max]).range([height, 0]);
    } else {
      this.yScale.domain([min, max]).range([height, 0]);
    }
  }

  protected updateYAxis(): void {
    if (!this.yAxisGroup || !this.yScale) return;

    if (this.title === 'Sleep_Stages') {
      this.yAxisGroup.selectAll('*').remove();
      return;
    }

    const { min, max } = this.getMinAndMax();
    this.yAxisGroup
      .attr(
        'transform',
        `translate(${this.graphSettings.chartLayout.margins.left * 2 - 30}, ${
          this.graphSettings.chartLayout.margins.top
        })`
      )
      .style('pointer-events', 'none')
      .call(
        axisLeft(this.yScale)
          .tickFormat((d) => this.getYTickFormat(d))
          .tickValues([min, max])
          .tickSizeOuter(0)
          .tickSizeInner(0)
      )
      .raise();

    this.yAxisGroup
      .selectAll('.tick text')
      .style('font-size', this.svgSizes?.height < 30 ? '7px' : '9px')
      .style('color', 'black');

    setupYAxisDrag(this as unknown as YAxisPanContext);
  }

  protected animateRedraw(duration = 150): void {
    if (!this.data?.length) return;

    this.updateScales();

    // Animate Y-axis
    const { min: yMin, max: yMax } = this.getMinAndMax();
    this.applyTransition(this.yAxisGroup, duration)
      .call(
        axisLeft(this.yScale)
          .tickFormat((d) => this.getYTickFormat(d))
          .tickValues([yMin, yMax])
          .tickSizeOuter(0)
          .tickSizeInner(0)
      )
      .selection()
      .selectAll('.tick text')
      .style('font-size', '9px')
      .style('color', 'black');

    // Waveform redraw.
    //
    // IMPORTANT: when the fast-path is active (rawYData populated), `this.data`
    // is intentionally a LABEL_DATA_CAP-downsampled coord array (~300 points)
    // — `paintChart` builds it only for label/tooltip lookup; the actual
    // drawing in `updateLine` uses `buildFastPath` over the full-resolution
    // rawYData. Animating attr('d') here with d3's line() generator over
    // `this.data` would therefore replace the full-resolution path with a
    // smoothed low-resolution approximation. Worse, the 150ms transition
    // keeps re-applying the low-res output for the duration of the
    // animation, beating any follow-up `updateLine` that the
    // setSignalZoom-driven repaint queues. That's the "signal shape becomes
    // completely different after y-axis pan" bug.
    //
    // For fast-path signals: interrupt any pending transition on the line
    // and snap directly to the full-resolution path. The y-axis labels and
    // grid still animate smoothly, which carries the perceived feedback of
    // the pan. For non-fast-path signals (Position, full-night, etc.) keep
    // the original line-generator transition.
    const isMultiY = Array.isArray((this.data as any)?.[0]?.y);
    const useFastPath = !isMultiY && this.rawYData !== null && this.rawYData.length > 0;
    const useMultiYFastPath =
      isMultiY &&
      this.rawYData !== null &&
      this.rawYData.length > 0 &&
      Array.isArray(this.rawYData[0]);

    const linePath = this.graphGroup.selectAll<SVGPathElement, GraphPoint<SafeAny>[]>(
      'path.single-line'
    );

    if (useFastPath || useMultiYFastPath) {
      linePath.interrupt();
      const pathString = useFastPath ? this.buildFastPath() : this.buildFastMultiYPath();
      linePath.attr('d', pathString);
    } else {
      const lineGenerator = line<GraphPoint<SafeAny>>()
        .defined((p) => p.y !== null && !isNaN(p.y))
        .x((p) => this.xScale(p.x))
        .y((p) => this.yScale(this.getInvertedY(p.y)));

      this.applyTransition(linePath, duration).attr('d', (d) => lineGenerator(d));
    }

    // Smooth the grid too
    this.applyTransition(this.yGridLines.selectAll('line'), duration)
      .attr('y1', (d) => this.yScale(d))
      .attr('y2', (d) => this.yScale(d));
  }

  private applyTransition<T extends d3.Selection<any, any, any, any>>(
    selection: T,
    duration = 150,
    easeFn = easeCubicOut
  ): d3.Transition<any, any, any, any> {
    return selection.transition().duration(duration).ease(easeFn);
  }

  protected updateGrid(): void {
    if (!this.grid || !this.xGridLines || !this.xScale) return;

    const epoch =
      typeof (this as any).epoch === 'string' ? Number((this as any).epoch) : (this as any).epoch;
    const duration = this.duration;

    if (!Number.isFinite(duration) || duration <= 0) return;
    if (!Number.isFinite(epoch) || epoch < 0) return;

    const {
      left: gridLeft,
      yAxis: gridYAxis,
      title: gridTitle,
      top: gridTop
    } = this.graphSettings.chartLayout.margins;
    this.grid.attr('transform', `translate(${gridLeft + gridYAxis + gridTitle}, ${gridTop})`);

    const yMin = 0;
    const yMax = Math.max(
      0,
      this.svgSizes?.height - this.graphSettings.chartLayout.margins.top * 2
    );
    if (yMax <= 0) return;

    let xGridPixels: number[] = [];

    if (this.EPOCH_ALIGNED_GRID_WINDOW_SIZES.has(duration)) {
      const baseStartSec = Number.isFinite(this.studyStartSec)
        ? this.studyStartSec
        : this.startDatetime
          ? new Date(this.startDatetime).getTime() / 1000
          : NaN;

      if (Number.isFinite(baseStartSec)) {
        const startSec = baseStartSec + duration * epoch;

        if (Number.isFinite(startSec)) {
          const endSec = startSec + duration;
          const boundary = this.EPOCH_BOUNDARY_SEC;

          const TOLERANCE_SEC = 1e-6;
          const firstIndex = Math.ceil((startSec - baseStartSec) / boundary - TOLERANCE_SEC);
          const lastIndex = Math.floor((endSec - baseStartSec) / boundary + TOLERANCE_SEC);

          xGridPixels = Array.from({ length: Math.max(0, lastIndex - firstIndex + 1) }, (_, i) =>
            this.xScale(baseStartSec + (firstIndex + i) * boundary)
          ).filter((px) => Number.isFinite(px));
        }
      }
    }

    const xLines = this.xGridLines.selectAll<SVGLineElement, number>('line').data(xGridPixels);

    xLines
      .join(
        (enter) => enter.append('line'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('x1', (d) => d)
      .attr('x2', (d) => d)
      .attr('y1', yMin)
      .attr('y2', yMax);

    (this.xGridLines as any).raise?.();
  }

  protected getMaxDecimalsCount(): number {
    return signalAmplitudeRules[this.title]?.decimals ?? 1;
  }

  protected getYTickFormat(d: NumberValue): string {
    return formatFixed(+d, this.getMaxDecimalsCount());
  }

  protected getMinAndMax(): { min: number; max: number } {
    let min = this.mapping.min;
    let max = this.mapping.max;

    if (min === max) {
      min -= 1;
      max += 1;
    }

    if (INVERTIBLE_SIGNAL_TITLES.has(this.title?.toLowerCase()) && this.polarityInversion) {
      const invertedMin = -max;
      const invertedMax = -min;
      return { min: invertedMin, max: invertedMax };
    }

    return { min, max };
  }

  protected get plotHeight(): number {
    return this.svgSizes?.height - this.graphSettings.chartLayout.margins.top * 2;
  }

  protected clampToPlotBounds(y: number): number {
    return Math.max(0, Math.min(this.plotHeight, y));
  }

  protected getInvertedY(y: number): number {
    return INVERTIBLE_SIGNAL_TITLES.has(this.title?.toLowerCase()) && this.polarityInversion
      ? -y
      : y;
  }

  protected handleRestrictedAccess(): void {
    if (this.handleRestrictedEventUpdate) {
      this.handleRestrictedEventUpdate();
    }
  }

  protected updateEvents(): void {
    if (!this.isEditable || !this.areEventsEnabled) return;

    const handleRestrictedAccess = () => this.handleRestrictedAccess();

    this.eventsGroup.attr(
      'transform',
      `translate(${
        this.graphSettings.chartLayout.margins.left +
        this.graphSettings.chartLayout.margins.yAxis +
        this.graphSettings.chartLayout.margins.title
      }, ${this.graphSettings.chartLayout.margins.top})`
    );

    const { min, max } = this.getMinAndMax();

    const [winStart, winEnd] = this.xScale.domain();

    const signalHeight = this.svgSizes?.height;

    const startSec = (d: any) => {
      if (d._startCache !== d.start) {
        d._startCache = d.start;
        d.startSec = new Date(d.start).getTime() / 1000;
      }
      return d.startSec as number;
    };

    const visibleEvents = (this.events ?? []).filter((event: any) => {
      const eventStartTime = startSec(event);
      const eventEndTime = eventStartTime + (event.duration ?? 0);

      const startsBeforeWindowEnds = eventStartTime <= winEnd;
      const endsAfterWindowStarts = eventEndTime >= winStart;

      return startsBeforeWindowEnds && endsAfterWindowStarts;
    });

    const events = this.eventsGroup
      .selectAll<SVGGElement, any>('g.event')
      .data(visibleEvents, (d: any) => d.id);

    const exitingEvents = events.exit();
    if (this.activeEvent && exitingEvents.data().some((d: any) => d.id === this.activeEvent.id)) {
      this.hideTooltip();
    }
    exitingEvents.remove();

    const eventsEnter = events
      .enter()
      .append('g')
      .classed('event', true)
      .style('cursor', !this.isReadOnly ? 'pointer' : 'not-allowed')
      .on('mouseenter', this.eventMouseEnterHandler)
      .on('mouseleave', this.hideTooltip);

    eventsEnter.append('rect').classed('move', true);
    eventsEnter.append('text');
    eventsEnter.append('rect').classed('resize', true).classed('resize-left', true);
    eventsEnter.append('rect').classed('resize', true).classed('resize-right', true);

    const merged = eventsEnter
      .merge(events as any)
      .attr('event-id', (d: any) => d.id)
      .attr('transform', (d: any) => `translate(${this.xScale(startSec(d))}, ${this.yScale(max)})`)
      .attr('x', (d: any) => this.xScale(startSec(d)))
      .attr('y', () => this.yScale(max))
      .attr('width', (d: any) => this.xScale(startSec(d) + d.duration) - this.xScale(startSec(d)));

    merged
      .select<SVGRectElement>('rect.move')
      .classed('moveable', (d: any) => !this.protectedEventTypes.includes(d.eventType))
      .attr('x', 0)
      .attr('y', 0)
      .attr('height', this.yScale(min) - this.yScale(max))
      .attr('width', (d: any) => this.xScale(startSec(d) + d.duration) - this.xScale(startSec(d)))
      .attr('fill', (d: any) => {
        const baseColor = this.getBaseColor(d);
        if (d.isRespInWakeUnstaged) return color(baseColor)?.darker(3).formatHex() ?? baseColor;
        return baseColor;
      })
      .attr('stroke', (d: any) => this.getBaseColor(d))
      .style('cursor', !this.isReadOnly ? 'pointer' : 'not-allowed')
      .on('click', !this.isReadOnly ? this.showEventDetails : handleRestrictedAccess);

    if (!this.isReadOnly) {
      merged.each((d: any, i: number, nodes: any) => {
        if (!this.protectedEventTypes.includes(d.eventType)) {
          select(nodes[i])
            .select('rect.move')
            .call(this.eventMove as any);
        }
      });
    } else {
      merged.select('rect.move').on('click', handleRestrictedAccess);
    }

    merged.each((d: any, i: number, nodes: any) => {
      const g = select(nodes[i]);
      const text = g
        .select<SVGTextElement>('text')
        .attr('x', 3)
        .attr('text-anchor', 'start')
        .raise();

      const rectWidth = this.xScale(startSec(d) + d.duration) - this.xScale(startSec(d));

      if (this.title === 'Sleep_Stages') {
        const rectHeight = this.yScale(min) - this.yScale(max);
        const rectWidth =
          this.xScale(new Date(d.start).getTime() / 1000 + d.duration) -
          this.xScale(new Date(d.start).getTime() / 1000);
        const areaFactor = Math.sqrt(rectHeight * rectWidth);
        const mainFontSize = Math.max(10, Math.min(4, areaFactor * 0.2));
        const subFontSize = Math.max(10, Math.min(2, areaFactor * 0.1));

        text.attr('y', 20);
        text.selectAll('tspan').remove();

        const getStageLabel = (label: string, width: number): string => {
          if (['N1', 'N2', 'N3'].includes(label)) return label;
          if (width < 60) return label.charAt(0);
          return label;
        };

        const label = getStageLabel(getTranslatedValueWithArtifact(d.eventType), rectWidth);

        text
          .append('tspan')
          .attr('x', 2)
          .attr('dy', -8)
          .style('font-size', `${mainFontSize}px`)
          .text(label);

        const studyStart = new Date(this.startDatetime).getTime() / 1000;
        const baselineEpoch = Math.floor((startSec(d) - studyStart) / 30);
        const subText = rectWidth < 60 ? `${baselineEpoch}` : `(${baselineEpoch})`;

        text
          .append('tspan')
          .attr('x', 1)
          .attr('dy', '1.2em')
          .style('font-size', `${subFontSize}px`)
          .text(`${subText}`);
      } else {
        if (this.isWideWindow() || signalHeight < 30) {
          text.text('');
        } else {
          text
            .attr('y', 9)
            .style('font-size', `9px`)
            .text(
              `${getTranslatedValueWithArtifact(d.eventType)} (${formatTimeValue(d.duration)} s)`
            );
        }
      }

      if (this.protectedEventTypes.includes(d.eventType)) return;

      const resizeWidth = 4;
      const w = rectWidth;

      g.select<SVGRectElement>('rect.resize-left')
        .attr('height', this.yScale(min) - this.yScale(max))
        .attr('width', resizeWidth)
        .attr('x', -(resizeWidth / 2))
        .attr('y', 0)
        .style('cursor', !this.isReadOnly ? 'ew-resize' : 'not-allowed');

      g.select<SVGRectElement>('rect.resize-right')
        .attr('height', this.yScale(min) - this.yScale(max))
        .attr('width', resizeWidth)
        .attr('x', w - resizeWidth / 2)
        .attr('y', 0)
        .style('cursor', !this.isReadOnly ? 'ew-resize' : 'not-allowed');

      const anyD = d as any;

      const leftDatum = anyD._resizeLeftDatum ?? (anyD._resizeLeftDatum = {} as any);
      const rightDatum = anyD._resizeRightDatum ?? (anyD._resizeRightDatum = {} as any);

      leftDatum.id = d.id;
      leftDatum.start = d.start;
      leftDatum.duration = d.duration;
      leftDatum.eventType = d.eventType;
      leftDatum.direction = 'left';

      rightDatum.id = d.id;
      rightDatum.start = d.start;
      rightDatum.duration = d.duration;
      rightDatum.eventType = d.eventType;
      rightDatum.direction = 'right';

      g.select('rect.resize-left').datum(leftDatum);
      g.select('rect.resize-right').datum(rightDatum);

      if (!this.isReadOnly) {
        g.selectAll('rect.resize').call(this.eventResize as any);
      } else {
        g.selectAll('rect.resize').on('click', handleRestrictedAccess);
      }
    });

    this.applyLeftBarGradient();
  }

  protected handleGlobalMouseMove(event: MouseEvent): void {
    this.updateTooltip(event);
  }

  protected eventMouseEnterHandler(event: MouseEvent, data: SignalEvent): void {
    this.activeEvent = data;
    this.showTooltip();
    this.updateTooltip(event);
    window.addEventListener('mousemove', this.handleGlobalMouseMove, true);
  }

  protected isBadData = (d): boolean => d.eventType.includes('Artifact');

  private resolveEventColor(color: string): string {
    const normalized = color?.trim().toLowerCase();
    const isBlack = normalized === '#000000' || normalized === '#000' || normalized === 'black';

    return isBlack ? '#666666' : color;
  }

  protected eventDragSubjectHandler(
    _event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent
  ) {
    let node = this.eventsGroup.select(`g[event-id="${data.id}"]`);

    if (node.empty()) {
      let foundNode: Element | null = null;
      this.eventsGroup.selectAll('g.event').each(function (this: SafeAny, d: SafeAny) {
        if (d && d.id === data.id && this) {
          foundNode = this as Element;
        }
      });
      if (foundNode) {
        node = select(foundNode);
      }
    }

    return node;
  }

  protected hideTooltip(): void {
    if (this.isEventDragActive) return;

    this.stopHoverTooltipTracking();
    super.hideTooltip();
  }

  private stopHoverTooltipTracking(): void {
    window.removeEventListener('mousemove', this.handleGlobalMouseMove, true);
    this.activeEvent = null;
  }

  protected getEventStartSec(data: { start: string; startSec?: number }): number {
    return typeof data.startSec === 'number'
      ? data.startSec
      : new Date(data.start).getTime() / 1000;
  }

  private showDragTooltip(clientX: number, clientY: number, html: string): void {
    this.tooltip
      .style('display', 'flex')
      .style('top', `${clientY + 5}px`)
      .style('left', `${clientX + 5}px`)
      .html(html);

    requestAnimationFrame(() => {
      this.tooltip.style(
        'transform',
        clientX + (this.tooltip.node()?.getBoundingClientRect().width || 0) > window.innerWidth - 20
          ? 'translateX(-100%)'
          : ''
      );
    });
  }

  protected buildLiveDragTooltipContent(
    startSec: number,
    durationSec: number,
    _eventType?: string
  ): string {
    return buildLiveEventTooltipContent(startSec, durationSec);
  }

  protected eventStartMovingHandler(
    event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent
  ): void {
    event.subject.attr('drag-start', event.sourceEvent.offsetX);
    this.clearEventDetails();
    this.stopHoverTooltipTracking();
    this.isEventDragActive = true;
    this.showDragTooltip(
      event.sourceEvent.clientX,
      event.sourceEvent.clientY,
      this.buildLiveDragTooltipContent(this.getEventStartSec(data), data.duration, data.eventType)
    );
  }

  protected getBaseColor(d: any): string {
    if (this.isBadData(d)) return this.artifactColor;

    if (this.eventColors[d.eventType]) return this.eventColors[d.eventType];

    if (d.eventType in baseEventColor) {
      return baseEventColor[d.eventType as SleepStage];
    }

    return this.color;
  }

  protected eventMovingHandler(
    event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent
  ): void {
    if (!event.subject || typeof event.subject.node !== 'function' || !event.subject.node()) {
      return;
    }

    const nextX =
      +event.subject.attr('x') + event.sourceEvent.offsetX - +event.subject.attr('drag-start');

    event.subject
      .raise()
      .attr('moving', true)
      .attr('transform', `translate(${nextX}, ${event.subject.attr('y')})`);

    this.updateEventDetailsPosition(event.subject);

    this.showDragTooltip(
      event.sourceEvent.clientX,
      event.sourceEvent.clientY,
      this.buildLiveDragTooltipContent(this.xScale.invert(nextX), data.duration, data.eventType)
    );
  }

  protected eventEndMovingHandler(
    event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent
  ): void {
    if (!event.subject || typeof event.subject.node !== 'function' || !event.subject.node()) {
      this.isEventDragActive = false;
      this.hideTooltip();
      return;
    }

    if (!event.subject.attr('moving')) {
      this.isEventDragActive = false;
      this.hideTooltip();
      return;
    }

    const nextX =
      +event.subject.attr('x') + event.sourceEvent.offsetX - +event.subject.attr('drag-start');
    const start = new Date(this.xScale.invert(nextX) * 1000).toISOString();
    this.eventBus.publish(Events.updateEvents, {
      ...data,
      start
    });

    event.subject.attr('drag-start', null).attr('moving', null).attr('x', nextX).lower();
    this.isEventDragActive = false;
    this.hideTooltip();
  }

  protected eventStartResizingHandler(
    event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent
  ): void {
    event.subject.attr('drag-start', event.sourceEvent.offsetX);
    this.clearEventDetails();
    this.stopHoverTooltipTracking();
    this.isEventDragActive = true;
    this.showDragTooltip(
      event.sourceEvent.clientX,
      event.sourceEvent.clientY,
      this.buildLiveDragTooltipContent(this.getEventStartSec(data), data.duration, data.eventType)
    );
  }

  protected eventResizingHandler(
    event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent & { direction: 'left' | 'right' }
  ): void {
    if (!event.subject || typeof event.subject.node !== 'function' || !event.subject.node()) {
      return;
    }
    event.subject.raise();
    const mainRect = event.subject.select('.move');

    if (data.direction === 'right') {
      const nextWidth =
        +event.subject.attr('width') +
        (event.sourceEvent.offsetX - +event.subject.attr('drag-start'));
      if (nextWidth < 5) {
        return;
      }
      mainRect.attr('width', nextWidth);
      event.subject.select('.resize-right').attr('x', nextWidth - 2);

      const startSec = this.getEventStartSec(data);
      const durationSec = this.xScale.invert(+event.subject.attr('x') + nextWidth) - startSec;
      this.showDragTooltip(
        event.sourceEvent.clientX,
        event.sourceEvent.clientY,
        this.buildLiveDragTooltipContent(startSec, durationSec, data.eventType)
      );
      return;
    }

    const deltaX = event.sourceEvent.offsetX - +event.subject.attr('drag-start');
    const nextWidth = +event.subject.attr('width') - deltaX;

    if (nextWidth < 5) {
      return;
    }

    mainRect.attr('width', nextWidth);
    const nextX = +event.subject.attr('x') + deltaX;
    event.subject.attr('transform', `translate(${nextX}, ${event.subject.attr('y')})`);

    event.subject.select('.resize-right').attr('x', nextWidth - 2);

    this.updateEventDetailsPosition(event.subject);

    const startSec = this.xScale.invert(nextX);
    const durationSec = this.xScale.invert(nextX + nextWidth) - startSec;
    this.showDragTooltip(
      event.sourceEvent.clientX,
      event.sourceEvent.clientY,
      this.buildLiveDragTooltipContent(startSec, durationSec, data.eventType)
    );
  }

  protected eventEndResizingHandler(
    event: D3DragEvent<SafeAny, SafeAny, SafeAny>,
    data: SignalEvent & {
      direction: 'left' | 'right';
    }
  ): void {
    if (!event.subject || typeof event.subject.node !== 'function' || !event.subject.node()) {
      return;
    }

    if (data.direction === 'right') {
      let nextWidth =
        +event.subject.attr('width') +
        (event.sourceEvent.offsetX - +event.subject.attr('drag-start'));

      if (nextWidth < 5) {
        nextWidth = 5;
      }

      const nextX2 = +event.subject.attr('x') + Math.max(nextWidth, 5);
      const duration =
        (new Date(this.xScale.invert(nextX2) * 1000).getTime() - new Date(data.start).getTime()) /
        1000;
      this.eventBus.publish(Events.updateEvents, {
        ...data,
        duration
      });
      this.isEventDragActive = false;
      this.hideTooltip();
      return;
    }

    const deltaX = event.sourceEvent.offsetX - +event.subject.attr('drag-start');
    let nextWidth = +event.subject.attr('width') - deltaX;

    if (nextWidth < 5) {
      nextWidth = 5;
    }

    const nextX = +event.subject.attr('x') + deltaX;
    const nextX2 = nextX + Math.max(nextWidth, 5);
    const start = new Date(this.xScale.invert(nextX) * 1000);
    const duration =
      (new Date(this.xScale.invert(nextX2) * 1000).getTime() - start.getTime()) / 1000;

    this.eventBus.publish(Events.updateEvents, {
      ...data,
      duration,
      start: start.toISOString()
    });

    event.subject.lower();
    this.isEventDragActive = false;
    this.hideTooltip();
  }

  protected duringBrushingHandler(event: D3BrushEvent<SafeAny>): void {
    if (!this.isEditable || this.isReadOnly || !this.areEventsEnabled) return;

    const selection = event.selection as [number, number] | null;
    if (!selection || selection[0] === selection[1]) return;

    const sourceEvent = event.sourceEvent as MouseEvent | undefined;
    if (!sourceEvent) return;

    const startSec = this.xScale.invert(selection[0]);
    const durationSec = this.xScale.invert(selection[1]) - startSec;
    const eventType = this.acceptedEventTypes.length === 1 ? this.acceptedEventTypes[0] : undefined;

    this.showDragTooltip(
      sourceEvent.clientX,
      sourceEvent.clientY,
      this.buildLiveDragTooltipContent(startSec, durationSec, eventType)
    );
  }

  protected endBrushingHandler(event: D3BrushEvent<SafeAny>): void {
    this.hideTooltip();

    if (event.selection?.[0] === event.selection?.[1]) {
      return;
    }

    this.selectedBrush = event.selection;

    if (!this.isEditable || this.isReadOnly || !this.areEventsEnabled) {
      this.clearBrush();

      return;
    }

    const tempEventID = `temp_${uuidv4()}`;
    this.clearEventDetails();
    if (this.acceptedEventTypes.length <= 1) {
      this.eventBus.publish(Events.insertEvents, {
        id: tempEventID,
        start: new Date(this.xScale.invert(event.selection?.[0] as number) * 1000).toISOString(),
        duration:
          this.xScale.invert(event.selection?.[1] as number) -
          this.xScale.invert(event.selection?.[0] as number)
      });
      this.clearBrush();
    } else {
      const signalEvent: SignalEvent = {
        id: tempEventID,
        eventType: this.title,
        start: new Date(this.xScale.invert(event.selection?.[0] as number) * 1000).toISOString(),
        startEpoch: 0,
        endEpoch: 0,
        duration:
          this.xScale.invert(event.selection?.[1] as number) -
          this.xScale.invert(event.selection?.[0] as number)
      };

      const [left, max] = pointer(event.sourceEvent);

      this.showEventDetails(event.sourceEvent, signalEvent, {
        left,
        top: max,
        color: this.color,
        selection: event.selection
      });
    }
  }

  protected showEventDetails(event: PointerEvent, data: SignalEvent, newEvent?: any): void {
    const { t } = i18n.global;

    this.eventBus.publish(EVENT_DETAIL_STATUS, true);

    event.stopPropagation();
    if (!this.eventDetails) {
      return;
    }
    const actionsData = [...this.acceptedEventTypes];

    this.lastActiveEvent = data;

    if (!this.protectedEventTypes.includes(data.eventType) && !newEvent) {
      actionsData.push(t('remove'));
      actionsData.push(t('removeAll'));
    }
    const actions = this.eventDetails.selectAll('.action').data(
      actionsData
        .map((eventType, index) => ({
          key:
            eventType === t('remove')
              ? t('delete')
              : eventType === t('removeAll')
                ? t('removeAll')
                : String(index + 1),
          displayKey:
            eventType === t('remove')
              ? t('delete')
              : eventType === t('removeAll')
                ? ''
                : (this.shortcutKeys[
                    EVENT_TYPE_TO_SHORTCUT_ID[eventType] ??
                      (eventType.startsWith('Artifact') ? 'artifact' : '')
                  ] ?? ''),
          label: getTranslatedValueWithArtifact(eventType),
          order:
            this.protectedEventTypes.indexOf(eventType) !== -1
              ? this.protectedEventTypes.indexOf(eventType)
              : Number.MAX_SAFE_INTEGER
        }))
        .sort((a, b) => a.order - b.order)
    );

    actions
      .enter()
      .append('div')
      .classed('action', true)
      .merge(actions as SafeAny)
      .classed('remove', (d) => d.key === t('delete'))
      .classed('remove-all', (d) => d.key === t('removeAll'))
      .on('click', (_event, d) => {
        if (d.key === t('delete')) {
          this.eventBus.publish(Events.deleteEvents, { id: data.id });
          return;
        }
        if (d.key === t('removeAll')) {
          this.eventBus.publish(Events.deleteAllEvents, { eventType: data.eventType });
          return;
        }
        const eventIndex = Number.parseInt(d.key.toString()) - 1;
        if (newEvent) {
          this.eventBus.publish(Events.insertEvents, {
            id: data.id,
            start: new Date(
              this.xScale.invert(newEvent.selection?.[0] as number) * 1000
            ).toISOString(),
            duration:
              this.xScale.invert(newEvent.selection?.[1] as number) -
              this.xScale.invert(newEvent.selection?.[0] as number),
            eventIndex
          });
        } else {
          this.eventBus.publish(Events.updateEvents, {
            ...data,
            eventType: d.label,
            eventIndex
          });
        }
      })
      .each((action, i, items) => {
        const spans = select(items[i])
          .selectAll('span')
          .data([
            { class: 'key', value: action.displayKey },
            { class: 'label', value: action.label }
          ]);

        spans
          .enter()
          .append('span')
          .merge(spans as SafeAny)
          .attr('class', (d) => d.class)
          .text((d) => d.value);

        spans.exit().remove();
      });
    actions.exit().remove();

    let topPosition = `${newEvent?.top - document.documentElement.scrollTop || 0}px`;

    if (!newEvent) {
      let eventGroup = this.eventsGroup.select(`g[event-id="${data.id}"]`).node() as Element | null;

      if (!eventGroup) {
        eventGroup = this.eventsGroup
          .selectAll('g')
          .filter((d: SafeAny) => d && d.id === data.id)
          .node() as Element | null;
      }

      if (eventGroup) {
        const { top } = eventGroup.getBoundingClientRect();
        topPosition = top ? `${top + document.documentElement.scrollTop}px` : topPosition;
      }
    }

    const rawColor = newEvent
      ? newEvent.color
      : this.isBadData(data)
        ? this.BAD_DATA_COLOR
        : this.color;

    const tooltipBg = this.resolveEventColor(rawColor);

    this.eventDetails
      .style('top', topPosition)
      .style('display', 'flex')
      .style('background-color', tooltipBg);

    this.eventDetails.node()?.setAttribute('signal-name', this.title);

    if (newEvent) {
      this.eventDetails.style('left', `${newEvent.left - 10}px`);
    } else {
      this.updateEventDetailsPosition(this.eventsGroup.select(`g[event-id="${data.id}"]`));
    }

    select('body').on('click', (bodyEvent: PointerEvent) => {
      this.clearEventSelection();
      if (event.target === bodyEvent.target) {
        return;
      }
      if (this.brushGroup && this.brush) {
        this.brushGroup.call(this.brush.move, [0, 0]);
      }
    });
    select('body').on('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.clearEventSelection();
      }
    });
  }

  protected clearEventSelection(): void {
    this.clearEventDetails();
    this.clearBrush();
    this.eventBus.publish(EVENT_DETAIL_STATUS, false);
  }

  protected updateEventDetailsPosition(eventSelection: Selection): void {
    if (!this.eventDetails || !eventSelection.node()) {
      return;
    }

    const eventGroup = eventSelection.node() as HTMLElement;
    const tooltipNode = this.eventDetails.node();

    if (!tooltipNode) {
      return;
    }

    const eventRect = eventGroup.getBoundingClientRect();
    const tooltipHeight = tooltipNode.offsetHeight ?? 0;
    const tooltipWidth = tooltipNode.offsetWidth ?? 0;
    const viewportWidth = window?.innerWidth;

    let clippingContainerRect: DOMRect | null = null;
    let currentElement: HTMLElement | null = eventGroup.parentElement;

    while (currentElement) {
      const style = window.getComputedStyle(currentElement);
      if (
        style.overflow !== 'visible' ||
        style.overflowX !== 'visible' ||
        style.overflowY !== 'visible'
      ) {
        clippingContainerRect = currentElement.getBoundingClientRect();
        break;
      }

      if (currentElement.tagName.toLowerCase() === 'svg') {
        clippingContainerRect = currentElement.getBoundingClientRect();
        break;
      }

      currentElement = currentElement.parentElement;
    }

    if (!clippingContainerRect) {
      clippingContainerRect = new DOMRect(0, 0, viewportWidth, window.innerHeight);
    }

    const intersectionLeft = Math.max(eventRect.left, clippingContainerRect.left);
    const intersectionRight = Math.min(eventRect.right, clippingContainerRect.right);

    let visibleEventX: number;
    let visibleEventWidth: number;

    if (intersectionRight <= intersectionLeft) {
      visibleEventX = clippingContainerRect.left;
      visibleEventWidth = 0;
    } else {
      visibleEventX = intersectionLeft;
      visibleEventWidth = intersectionRight - intersectionLeft;
    }

    const spaceAbove = eventRect.y;

    if (spaceAbove >= tooltipHeight) {
      this.eventDetails
        .style('top', `${eventRect.y - tooltipHeight - 5}px`)
        .style('transform', 'translateY(0)');
    } else {
      this.eventDetails
        .style('top', `${eventRect.y + eventRect.height + 5}px`)
        .style('transform', 'translateY(0)');
    }

    let leftPosition: number;

    if (visibleEventWidth > 0) {
      leftPosition = visibleEventX + visibleEventWidth / 2 - tooltipWidth / 2;
    } else {
      leftPosition = clippingContainerRect.left;
    }

    if (leftPosition < 0) {
      leftPosition = 0;
    } else if (leftPosition + tooltipWidth > viewportWidth) {
      leftPosition = viewportWidth - tooltipWidth;
    }

    if (tooltipWidth > viewportWidth) {
      leftPosition = 0;
    }

    this.eventDetails.style('left', `${leftPosition}px`).style('transform', 'translateX(0)');
  }

  protected clearEventDetails(): void {
    this.eventDetails?.node()?.removeAttribute('signal-name');
    this.eventDetails?.style('display', null).selectChildren().remove();
    select('body').on('click', null).on('keydown', null);
  }

  protected getListOfLabels(data: GraphPoint<number>[], labelSize: number): GraphPoint<number>[] {
    if (!data.length) return [];

    const plotW =
      this.svgSizes.width -
      this.graphSettings.chartLayout.margins.left * 2 -
      this.graphSettings.chartLayout.margins.yAxis -
      this.graphSettings.chartLayout.margins.zoomComponent;

    const [domainStart, domainEnd] = this.xScale.domain();
    const bucketDurationSec = (labelSize / plotW) * (domainEnd - domainStart);

    if (!Number.isFinite(bucketDurationSec) || bucketDurationSec <= 0) {
      return [data[0]];
    }

    const result: GraphPoint<number>[] = [];
    let lastBucket: number | null = null;

    for (const point of data) {
      const bucket = Math.floor(point.x / bucketDurationSec);
      if (bucket !== lastBucket) {
        result.push(point);
        lastBucket = bucket;
      }
    }

    return result;
  }

  protected buildTooltipContent(signalData: GraphPoint<SafeAny>): string {
    const { t } = i18n.global;

    const y = signalData.rawY ?? signalData.y;

    const decimals = this.getMaxDecimalsCount();
    const formattedY = Array.isArray(y)
      ? y.map((val) => formatFixed(val, decimals)).join('/')
      : formatFixed(y as number, decimals);

    let tooltipHtml = '';

    if (this.title !== 'Sleep_Stages') {
      tooltipHtml = `<p><span>${t('time')}</span>: <strong>${utcFormat('%H:%M:%S')(
        new Date(signalData.x * 1000)
      )}</strong></p>
       <p><span>${this.title}</span>: <strong>${formattedY}${this.mapping.unit}</strong></p>`;
    }

    if (this.activeEvent) {
      tooltipHtml += buildEventTooltipContent(this.activeEvent);
    }

    return tooltipHtml;
  }
}
